import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';

import { useAuthStore } from './stores/authStore';
import { useSpaceStore } from './stores/spaceStore';
import { useThemeStore } from './stores/themeStore';
import { usePushNotifications } from './hooks/usePushNotifications';

import Layout from './components/layout/Layout';
import BottomNav from './components/layout/BottomNav';

import Home from './pages/Home';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import LogExpense from './pages/LogExpense';
import PersonalDashboard from './pages/PersonalDashboard';
import SharedDashboard from './pages/SharedDashboard';
import Settings from './pages/Settings';
import OnboardingSpace from './pages/onboarding/OnboardingSpace';
import OnboardingMembers from './pages/onboarding/OnboardingMembers';
import OnboardingCategories from './pages/onboarding/OnboardingCategories';
import OnboardingInvite from './pages/onboarding/OnboardingInvite';
import OnboardingJoin from './pages/onboarding/OnboardingJoin';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  const { isHydrating } = useSpaceStore();
  
  if (isLoading || (user && isHydrating)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/30 animate-pulse" />
          <p className="text-muted text-sm">Loading PairTab...</p>
        </div>
      </div>
    );
  }
  
  return user ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  const { initTheme } = useThemeStore();
  const { checkSession, user } = useAuthStore();
  const { restoreCurrentSpace, clearCurrentSpace } = useSpaceStore();
  const { registerSW, subscribe, permission } = usePushNotifications();

  useEffect(() => {
    initTheme();
    checkSession();
    registerSW();
  }, [initTheme, checkSession, registerSW]);

  useEffect(() => {
    if (user) {
      restoreCurrentSpace();
      return;
    }

    clearCurrentSpace();
  }, [user, restoreCurrentSpace, clearCurrentSpace]);

  // Prompt for push on first authenticated visit
  useEffect(() => {
    if (user && permission === 'default') {
      const hasPrompted = localStorage.getItem('pairtab_push_prompted');
      if (!hasPrompted) {
        // Small delay so the user sees the app first
        const timer = setTimeout(() => {
          subscribe();
          localStorage.setItem('pairtab_push_prompted', '1');
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, permission, subscribe]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1A2B3C',
            color: '#F5F9F7',
            border: '1px solid #2A3B4C'
          }
        }}
      />
      
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          <Route path="/onboarding/space" element={<ProtectedRoute><OnboardingSpace /></ProtectedRoute>} />
          <Route path="/onboarding/members" element={<ProtectedRoute><OnboardingMembers /></ProtectedRoute>} />
          <Route path="/onboarding/categories" element={<ProtectedRoute><OnboardingCategories /></ProtectedRoute>} />
          <Route path="/onboarding/invite" element={<ProtectedRoute><OnboardingInvite /></ProtectedRoute>} />
          <Route path="/onboarding/join/:token" element={<OnboardingJoin />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Home />
                <BottomNav />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/log" element={
            <ProtectedRoute>
              <Layout>
                <LogExpense />
                <BottomNav />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard/personal" element={
            <ProtectedRoute>
              <Layout>
                <PersonalDashboard />
                <BottomNav />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard/shared" element={
            <ProtectedRoute>
              <Layout>
                <SharedDashboard />
                <BottomNav />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout>
                <Settings />
                <BottomNav />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AnimatePresence>
    </div>
  );
}

export default App;
